import type { ButtonHTMLAttributes, ReactNode } from 'react';

/**
 * variant별 색·테두리. Tailwind가 클래스를 **정적 문자열로** 스캔하므로
 * `bg-${variant}` 같은 동적 조합 금지 — 반드시 완성형 리터럴 맵으로 둔다.
 */
const VARIANT_CLASS = {
  /**
   * 주 액션 — 목업 `.btn.primary`: 브랜드 그라데이션(135deg, brand-600→brand-500) + 발광 그림자,
   * hover는 색 교체가 아니라 `brightness(1.06)`.
   *
   * 평면(`bg-brand-600`)에서 그라데이션으로 바꾼 근거: 리스킨 1-B가 "primary는 평면 유지"로
   * 정한 이유가 **"`Button` 프리미티브와 위계 일관성"**이었는데, 지금은 그 프리미티브 자체를
   * 시안 정본으로 재정의하는 중이라 그 근거가 기준을 잃는다. 시안 9종 전부 그라데이션이다.
   * ⚠️ 후속 필요: `packages/report-composer`의 primary 5곳(StickyHeader 2·ReportWriteShell·
   * ComposeWritePageShell·composerWidgetTreeRender)은 아직 평면이라 이 커밋으로 갈린다.
   */
  primary:
    'border-transparent bg-gradient-to-br from-brand-600 to-brand-500 text-white shadow-brand-glow hover:brightness-[1.06]',
  /** 보조 액션 — 흰 배경 + 토큰 테두리(목업 기본 `.btn`. ink-700=slate-700, hover=surface-hover). */
  secondary: 'border-line bg-white text-ink-700 hover:bg-surface-hover hover:text-ink-900',
  /** 파괴적 액션(삭제 등). 목업에 전용 정의가 없어 primary의 채움 강도만 rose로 옮긴 것. */
  danger: 'border-transparent bg-rose-600 text-white hover:bg-rose-700',
  /**
   * 저강도 액션 — 목업 `.row-actions .ico` 방식: **평상시 무배경·투명 테두리**,
   * hover에서만 흰 박스가 테두리+그림자와 함께 떠오른다.
   * 회색 채움(`hover:bg-surface-hover`)이 아닌 게 핵심 — 평상시 화면을 바꾸지 않으므로
   * 지금 배경 없이 떠 있는 인라인·아이콘 버튼을 시각 변화 없이 이관할 수 있다.
   */
  ghost:
    'border-transparent bg-transparent text-ink-400 hover:border-line hover:bg-white hover:text-ink-700 hover:shadow-control-hover',
  /** 완료·승인 등 긍정 확정 액션. danger(rose)와 색상만 다른 같은 서열(채움 강도 동일). */
  success: 'border-transparent bg-emerald-600 text-white hover:bg-emerald-700',
  /**
   * 되돌릴 수 있는 경고성 액션(반려 요청·재발송 등) — 파괴적이지 않아 danger와 구분한다.
   * amber-600 + 흰 글자는 대비 3.4:1로 큰 글씨 기준만 충족한다. text-xs(xs·sm)에서 쓰면
   * 본문 대비 기준(4.5:1)에 못 미치니 작은 크기에는 secondary+amber 텍스트를 고려할 것.
   */
  warning: 'border-transparent bg-amber-600 text-white hover:bg-amber-700',
} as const;

/**
 * 높이는 실측 군집에 맞춘다(2026-07-28 apps/web raw 버튼 1159개 전수):
 * 38px 244곳 · 30~34px 124곳 · 42~46px 50곳 · 22~26px 45곳.
 */
const SIZE_CLASS = {
  /** 표 행 안 액션·아이콘 버튼(24px). */
  xs: 'h-6 gap-1 px-2 text-xs',
  /**
   * 행 액션·툴바(32px). 목업의 행 아이콘은 30px, 뷰 토글은 32px로 **시안 자체가 갈려 있어**
   * 8px 그리드와 터치 타깃을 따라 32px로 스냅했다(30px 대비 +2px).
   */
  sm: 'h-8 gap-1.5 px-3 text-xs',
  /** 목업 `.btn` 정본 — height:38px, padding:0 14px, font-size:14.5px, gap:7px. */
  md: 'h-[38px] gap-[7px] px-3.5 text-[14.5px]',
  /** 모바일 하단 CTA·전체폭 제출(44px = 터치 타깃 최소치). 목업에 대응 정의 없음. */
  lg: 'h-11 gap-2 px-4 text-[14.5px]',
} as const;

/**
 * `iconOnly`일 때 좌우 패딩을 지우고 정사각으로 만든다. 폭은 각 size의 높이와 같은 값을
 * **리터럴로** 둔다(`w-${...}` 동적 조합은 Tailwind가 스캔하지 못함).
 */
const ICON_SIZE_CLASS = {
  xs: 'w-6 px-0',
  sm: 'w-8 px-0',
  md: 'w-[38px] px-0',
  lg: 'w-11 px-0',
} as const;

/**
 * **역할 이름이 없는 색**. `Badge`의 tone과 같은 키는 같은 색 계열을 가리킨다 —
 * 같은 화면에서 배지와 버튼이 짝을 이루므로(예: `예약` 배지 옆 `점검 시작` 버튼) 어휘가
 * 갈리면 안 된다.
 *
 * Badge의 8색을 다 옮기지 않고 **역할 이름이 이미 있는 색은 뺀다**:
 * emerald=success · amber=warning · rose=danger · brand=primary · slate=secondary.
 * 그것까지 넣으면 같은 버튼을 두 가지로 말할 수 있게 되어(`variant="danger"` vs
 * `tone="rose"`) 프리미티브가 막으려던 드리프트를 프리미티브가 만들어낸다.
 * 남는 건 목업 세만틱 팔레트(blue/emerald/amber/rose/violet) 중 역할이 없는 blue·violet과,
 * Badge가 분류 색 부족으로 추가한 teal뿐이다.
 *
 * ⚠️ 도메인 상태 이름(`scheduled` 등)을 키로 쓰지 말 것 — 도메인→색 매핑은 **소비처 책임**이다
 * (Badge와 같은 규약: [[feedback-composer-no-domain-specific-widget-in-core]]).
 */
const TONE_CLASS = {
  blue: 'border-transparent bg-blue-600 text-white hover:bg-blue-700',
  violet: 'border-transparent bg-violet-600 text-white hover:bg-violet-700',
  teal: 'border-transparent bg-teal-600 text-white hover:bg-teal-700',
} as const;

export type ButtonVariant = keyof typeof VARIANT_CLASS;
export type ButtonTone = keyof typeof TONE_CLASS;
export type ButtonSize = keyof typeof SIZE_CLASS;

/**
 * tone 키 목록. 회귀 테스트가 이 집합을 잠근다 — 역할 이름이 이미 있는 색(rose=danger 등)을
 * 나중에 tone에 끼워 넣으면 테스트가 깨지면서 위 주석의 근거를 읽게 된다.
 */
export const BUTTON_TONES = Object.keys(TONE_CLASS) as ButtonTone[];

interface ButtonBaseProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** 크기. 기본 md. */
  size?: ButtonSize;
  /**
   * 아이콘만 있는 정사각 버튼. 좌우 패딩을 지우고 폭을 높이와 같게 맞춘다.
   * 라벨이 없으므로 **`aria-label`(또는 `title`)을 반드시 함께 넘길 것** — 접근 이름이 사라진다.
   */
  iconOnly?: boolean;
  /** 라벨·아이콘. */
  children?: ReactNode;
  /** 추가 클래스(폭·정렬 등). 기본 클래스 뒤에 붙어 같은 속성이면 우선. */
  className?: string;
}

/**
 * `variant`(역할)와 `tone`(색)은 **동시에 쓸 수 없다**. 둘 다 채움 색을 정하므로 함께 받으면
 * 한쪽이 조용히 무의미해지고, 그건 `TextButton`을 `variant="link"`로 합치지 않은 것과 같은
 * 종류의 거짓말이다. 타입으로 배타를 강제해 호출부에서 바로 걸리게 한다.
 *
 * 고를 때: 역할 이름이 있으면(`저장`=primary, `삭제`=danger …) **언제나 `variant`**.
 * 역할이 없는 도메인 색일 때만 `tone`(예: 예약 상태의 violet).
 */
export type ButtonProps =
  | (ButtonBaseProps & {
      /** 시각 변형. 기본 secondary(가장 흔한 보조 액션). */
      variant?: ButtonVariant;
      tone?: never;
    })
  | (ButtonBaseProps & {
      variant?: never;
      /** 역할 이름이 없는 색. Badge의 같은 tone과 짝을 이룬다. */
      tone: ButtonTone;
    });

/**
 * 버튼 시각 클래스만 빌려 쓰기 — `<Link>`·`<a>` 등 내비게이션 요소를 버튼처럼 보이게 할 때
 * 사용(새 탭 열기 등 링크 시맨틱 보존이 필요한 자리에서 Button 대신). Button 본체와 단일 소스.
 */
export function buttonClassName(
  variant: ButtonVariant = 'secondary',
  size: ButtonSize = 'md',
  className = '',
  iconOnly = false,
): string {
  return buttonShellClass(size, iconOnly, VARIANT_CLASS[variant], className);
}

/**
 * `buttonClassName`의 tone판 — 색만 다르고 치수·radius·포커스 링은 같은 정본을 쓴다.
 * `<Link>`·`<a>`를 tone 색 버튼처럼 보이게 할 때 사용.
 */
export function buttonToneClassName(
  tone: ButtonTone,
  size: ButtonSize = 'md',
  className = '',
  iconOnly = false,
): string {
  return buttonShellClass(size, iconOnly, TONE_CLASS[tone], className);
}

/**
 * 색을 뺀 버튼 껍데기 — `variant`/`tone` 두 경로가 **같은 치수·radius·포커스 링**을 쓰도록
 * 한 곳에 둔다(두 벌로 두면 한쪽만 고쳐져 갈라진다).
 *
 * 목업 `.btn` 전사: border-radius:10px(rounded-control) · font-weight:600(font-semibold) ·
 * letter-spacing:-.005em · transition:all .14s. transition은 색만이 아니라 all이어야
 * primary의 brightness와 ghost의 그림자가 같이 물린다.
 */
function buttonShellClass(
  size: ButtonSize,
  iconOnly: boolean,
  colorClass: string,
  className: string,
): string {
  const icon = iconOnly ? ` ${ICON_SIZE_CLASS[size]}` : '';
  return `inline-flex items-center justify-center rounded-control border font-semibold tracking-[-0.005em] transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 ${SIZE_CLASS[size]}${icon} ${colorClass} ${className}`;
}

/**
 * variant의 **색만** 빌려 쓰기 — 치수(높이·패딩)를 소비처가 의도적으로 따로 쥐고 있는 자리용.
 * `packages/report-composer`의 compose 셸처럼 아이콘/라벨 높이 맞춤이 별도로 튜닝된 곳에서
 * 색만 정본과 맞추려고 쓴다. **일반 버튼은 `Button`/`buttonClassName`을 쓸 것** — 이걸 쓰면
 * radius·높이·포커스 링이 정본과 갈린다.
 */
export function buttonVariantClass(variant: ButtonVariant): string {
  return VARIANT_CLASS[variant];
}

/**
 * 공용 버튼 — 앱 전역에 흩어진 버튼 변형을 수렴시키는 단일 소스(base Modal 선례).
 *
 * 보장하는 것:
 * - `type` 기본 `'button'` — 폼 안에서 의도치 않은 submit 방지(가장 흔한 버그).
 * - disabled 시 `opacity-50 cursor-not-allowed` + 포인터 이벤트는 유지(네이티브 disabled가 클릭 차단).
 * - 아이콘+라벨 정렬(inline-flex + gap)과 브랜드 포커스 링을 variant/tone 무관하게 일관 적용.
 *
 * 색 토큰(`brand`/`line`/`surface`)은 `packages/ui/tailwind-preset.js`가 소유하고, 앱은
 * `presets: [uiPreset]`으로 가져간다 — **apps/web·apps/admin 모두 등록 완료**(각 tailwind.config.js).
 * 새 앱을 추가하면 preset 등록을 잊지 말 것: 없으면 클래스만 붙고 CSS가 없어 조용히 무스타일이 된다.
 * 결정 `2026-07-27-ui-redesign-mockup-adoption.md`.
 */
export function Button(props: ButtonProps) {
  // 배타 유니온은 구조분해와 같이 쓰면 TS가 각 멤버를 좁히지 못한다(잘 알려진 한계).
  // 타입은 위 ButtonProps가 이미 강제하므로, 여기서는 합집합 형태로 한 번만 읽는다.
  const {
    variant,
    tone,
    size = 'md',
    type = 'button',
    iconOnly = false,
    className = '',
    children,
    ...rest
  } = props as ButtonBaseProps & { variant?: ButtonVariant; tone?: ButtonTone };

  const cls = tone
    ? buttonToneClassName(tone, size, className, iconOnly)
    : buttonClassName(variant ?? 'secondary', size, className, iconOnly);

  return (
    <button type={type} className={cls} {...rest}>
      {children}
    </button>
  );
}
